/**
 * Servicio para gestión de alumnos del profesor
 */

import {
  AlumnoDetalle,
  AlumnoProfesor,
  AlumnoProgreso,
  AsignarAlumnoRequest,
  DesasignarAlumnoRequest,
  UpdateAlumnoEdit,
} from "../../app/profesor/alumnos/dto/alumnos.dto";
import { apiClientWeb } from "../../lib/apiClientWeb";

export const alumnosProfesorService = {
  /**
   * Obtiene la lista de alumnos asignados al profesor
   * No necesita profesorId - usa el token JWT para identificar al profesor
   */
  getAlumnos: async (profesorId?: number): Promise<AlumnoProfesor[]> => {
    const response = await apiClientWeb.get(`/api/profesor/alumnos`);
    return response.data.data; // Backend devuelve { success, data, total }
  },

  /**
   * Obtiene el progreso de todos los alumnos
   * No necesita profesorId - usa el token JWT para identificar al profesor
   */
  getProgreso: async (profesorId?: number): Promise<AlumnoProgreso[]> => {
    const response = await apiClientWeb.get(`/api/profesor/alumnos`);
    return response.data.data; // Por ahora usa el mismo endpoint
  },

  /**
   * Obtiene el detalle completo de un alumno
   */
  getAlumnoDetalle: async (
    profesorId: number,
    alumnoId: number
  ): Promise<AlumnoDetalle> => {
    const response = await apiClientWeb.get(
      `/api/profesor/alumnos/${alumnoId}`
    );
    return response.data.data;
  },

  /**
   * Asigna un alumno al profesor
   */
  asignarAlumno: async (data: AsignarAlumnoRequest): Promise<void> => {
    await apiClientWeb.post("/api/profesor/alumnos/asignar", data);
  },

  /**
   * Desasigna un alumno del profesor
   */
  desasignarAlumno: async (data: DesasignarAlumnoRequest): Promise<void> => {
    await apiClientWeb.post("/api/profesor/alumnos/desasignar", data);
  },

  updateAlumno: async (
    id: number,
    data: Partial<UpdateAlumnoEdit>
  ): Promise<{ success: boolean; data: UpdateAlumnoEdit }> => {
    const response = await apiClientWeb.patch(
      `/api/profesor/alumnos/${id}`,
      data
    );
    return response.data; // Retorna { success, data }
  },

  desactivarAlumno: async (id: number) => {
    const response = await apiClientWeb.patch(`/api/profesor/alumnos/${id}`, {
      vigente: false,
    });
    return response.data;
  },

  /**
   * Obtiene todos los años escolares disponibles
   */
  getAniosEscolares: async (): Promise<
    { idAnioEscolar: number; nombre: string; orden: number }[]
  > => {
    const response = await apiClientWeb.get(
      `/api/profesor/alumnos/opciones/anios-escolares`
    );
    return response.data.data;
  },

  /**
   * Obtiene todos los cursos del colegio del profesor
   */
  getCursosByColegio: async (): Promise<
    {
      idCurso: number;
      nombre: string;
      anio: number;
      anioEscolarId: number;
      AnioEscolar: {
        idAnioEscolar: number;
        nombre: string;
        orden: number;
      };
    }[]
  > => {
    const response = await apiClientWeb.get(
      `/api/profesor/alumnos/opciones/cursos`
    );
    return response.data.data;
  },

  /**
   * Crea un nuevo alumno
   */
  createAlumno: async (data: {
    nombres: string;
    apellidos: string;
    email: string;
    rut?: string | null;
    cursoId?: number | null;
    anioEscolarId?: number | null;
    vigente?: boolean;
  }): Promise<{ success: boolean; data: any }> => {
    const response = await apiClientWeb.post(`/api/profesor/alumnos`, data);
    return response.data;
  },

  createAlumnosBulk: async (data: {
    alumnos: Array<{
      nombres: string;
      apellidos: string;
      email?: string;
      rut?: string | null;
    }>;
    cursoId?: number | null;
    anioEscolarId?: number | null;
    vigente?: boolean;
  }): Promise<{
    success: boolean;
    data: {
      exitosos: Array<{
        nombres: string;
        apellidos: string;
        email: string;
        passwordTemporal: string;
      }>;
      errores: Array<{
        nombres: string;
        apellidos: string;
        email?: string;
        error: string;
      }>;
    };
    resumen: {
      total: number;
      exitosos: number;
      errores: number;
    };
  }> => {
    const response = await apiClientWeb.post(
      `/api/profesor/alumnos/bulk`,
      data
    );
    return response.data;
  },
};
